import Link from "next/link";
import { Badge } from "./badge";

/**
 * Beitragskarte und Aufmacher (Musterkatalog M-084 „Intranet lesen“, Design-Guide `beitrag-karte.md`): ein Intranet-Beitrag
 * als Karte (Kategorie, Datum, Titel, Anriss) und als Aufmacher (erster Beitrag, großer Titel, Bild daneben). Struktur aus
 * den Blog-Blöcken `image-post-card-grid-with-category-meta` und `featured-story-hero-with-side-cards`; die Blöcke tragen
 * Fremd-Fotos und Autorenfotos, übernommen ist nur die Struktur. Kein Autor (Autorschaft ist kein Fachbegriff, r112), keine
 * Lesezähler (r177).
 *
 * Das Bild ist optional und kommt nur, wenn der Aufrufer eine geprüfte Adresse übergibt (http/https oder interner Pfad,
 * `bildFuerAnzeige`); ohne Bild steht die Karte ohne Bildfläche da, es gibt kein Platzhalterbild. Rein darstellend: der
 * Aufrufer liefert nur Beiträge, die die Person lesen darf (r110). `href` ist seit Paket I3 die Leseseite des Beitrags
 * (`/intranet/aktuelles/<id>`, Gast `/start/<id>`); ohne `href` ist der Titel reiner Text ohne toten Link.
 *
 * Abweichung AW-100 (1): Eigenbau aus der Struktur der Blog-Blöcke, kein Katalogblock 1:1; Kategorie als neutrales Badge,
 * kein Platzhalterbild, kein Autor (siehe `docs/ui-preline/abweichungen.json`).
 */
export type BeitragKarteDaten = {
  titel: string;
  anriss?: string | null;
  kategorie?: string | null;
  /** Fertiges Datum („10.10.2026“); der Aufrufer formatiert in fester Zeitzone. */
  datum: string;
  /** Geprüfte Bildadresse; `null` = keine Bildfläche. */
  bildUrl?: string | null;
  /** Ziel der Karte (Leseseite); fehlt = kein Link. */
  href?: string;
};

const TITEL_LINK = "hover:text-primary focus:outline-hidden focus:text-primary after:absolute after:inset-0";

function Meta({ kategorie, datum }: { kategorie?: string | null; datum: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {kategorie && <Badge ton="neutral">{kategorie}</Badge>}
      <span className="text-xs text-muted-foreground-1 tabular-nums">{datum}</span>
    </div>
  );
}

export function BeitragKarte({ titel, anriss, kategorie, datum, bildUrl, href }: BeitragKarteDaten) {
  return (
    <>
      {/* Preline: blog-and-articles/blog-and-articles-sub-blog-discovery-sections/blog-and-articles-cat-blog-listing-sections/image-post-card-grid-with-category-meta */}
      <article className="relative h-full flex flex-col bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden">
        {bildUrl && (
          <div className="aspect-video w-full overflow-hidden">
            {/* Bild nur mit geprüfter Adresse; dekorativ, der Titel trägt die Aussage. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className="size-full object-cover" src={bildUrl} alt="" loading="lazy" />
          </div>
        )}
        <div className="p-4 md:p-5 flex flex-col gap-y-2">
          <Meta kategorie={kategorie} datum={datum} />
          <h3 className="text-base font-semibold text-foreground">
            {href ? (
              <Link className={TITEL_LINK} href={href}>
                {titel}
              </Link>
            ) : (
              titel
            )}
          </h3>
          {anriss && <p className="text-sm text-muted-foreground-2 line-clamp-3">{anriss}</p>}
        </div>
      </article>
    </>
  );
}

export function BeitragAufmacher({ titel, anriss, kategorie, datum, bildUrl, href }: BeitragKarteDaten) {
  return (
    <>
      {/* Preline: blog-and-articles/blog-and-articles-sub-blog-discovery-sections/blog-and-articles-cat-blog-listing-sections/featured-story-hero-with-side-cards */}
      <article className={bildUrl ? "relative grid md:grid-cols-5 bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden" : "relative bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden"}>
        {bildUrl && (
          <div className="md:col-span-2 aspect-video md:aspect-auto overflow-hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className="size-full object-cover" src={bildUrl} alt="" loading="lazy" />
          </div>
        )}
        <div className={bildUrl ? "md:col-span-3 p-5 md:p-8 flex flex-col justify-center gap-y-3" : "p-5 md:p-8 flex flex-col justify-center gap-y-3"}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge ton="primaer">
              <svg className="shrink-0 size-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 17v5" />
                <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
              </svg>
              Aufmacher
            </Badge>
            {kategorie && <Badge ton="neutral">{kategorie}</Badge>}
            <span className="text-xs text-muted-foreground-1 tabular-nums">{datum}</span>
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {href ? (
              <Link className={TITEL_LINK} href={href}>
                {titel}
              </Link>
            ) : (
              titel
            )}
          </h2>
          {anriss && <p className="text-sm md:text-base text-muted-foreground-2">{anriss}</p>}
        </div>
      </article>
    </>
  );
}
